import { debounce, isUndefined } from 'lodash';
import { ref, reactive } from 'vue';
import type { Ref } from 'vue';

const useQsSelect = ({
  QsSelectRef,
  defaultTableView = false,
  getList,
  limit = 20, // 当不想要分页加载数据时，将limit传最大值如：5000
  emits, // 当需要在使用select组件时拿到整个选项，需要在使用useSelect中传递emits进来，并且保证传递的emits中存在changeItem事件，可参考select-supplier
  tabList,
  otherOptions, // 另外的选项
}: {
  QsSelectRef?: Ref<any>; // QsSelect的ref
  defaultTableView?: boolean; // 默认使用表格模式
  getList: (data: Record<string, any>, params: Record<string, any>) => any;
  limit?: number;
  emits?: (event: any, ...args: any[]) => void;
  tabList?: Ref<Recordable[]>;
  otherOptions?: Recordable[];
}) => {
  const selectItem = ref<Record<string, any>>({});
  const finished = ref<boolean>(false);
  const searchKey = ref<string>();
  const dataList = ref<Recordable[]>(otherOptions ?? []);
  const isTableView = ref<boolean>(defaultTableView);
  const pageParams = reactive<Record<string, any>>({
    page: 1,
    limit,
    totalCount: 0,
    layouts: [
      'PrevJump',
      'PrevPage',
      'Jump',
      'PageCount',
      'NextPage',
      'NextJump',
      'Total',
    ],
  });
  const activeTab = ref<any>(tabList?.value?.[0]?.value);
  // 搜索
  const search = (e: string) => {
    refresh(e);
  };
  // 刷新
  const refresh = async (e?: string) => {
    searchKey.value = e ?? '';
    pageParams.page = 1;
    await getOptionList();
    if (QsSelectRef?.value) {
      QsSelectRef?.value.setLoading(false);
    }
  };
  // 触底加载
  const load = () => {
    if (finished.value) return;
    pageParams.page += 1;
    getOptionList();
  };
  // 改变分页
  const pageChange = ({
    page,
    limit: vxeLimit,
  }: {
    page: number;
    limit: number;
  }) => {
    pageParams.page = page;
    pageParams.limit = vxeLimit;
    getOptionList();
  };
  const tabChange = (val: any) => {
    pageParams.page = 1;
    activeTab.value = val;
    dataList.value = [];
    getOptionList();
  };
  const changeView = debounce(() => {
    dataList.value = [];
    isTableView.value = !isTableView.value;
    refresh(searchKey.value);
  }, 300);
  // 获取整个item
  const change = (item: Record<string, any>) => {
    if (emits) {
      emits('change', item);
    }
  };
  // 根据id获取option的函数，用于默认值回显
  const getOptionById = (res: { data: { list: any[] } }) => {
    return new Promise<void>((resolve) => {
      const { list = [] }: { list: any[] } = res.data;
      const result = list?.length ? list[0] : {};
      resolve(result);
    });
  };
  const getOptionsByIdList = (res: { data: { list: any[] } }) => {
    return new Promise<any[]>((resolve) => {
      const { list = [] }: { list: any[] } = res.data;
      const result = list?.length ? list : [];
      resolve(result);
    });
  };
  // 分页获取选项列表
  const getOptionList = async () => {
    try {
      const data: Record<string, any> = {};
      if (searchKey.value) {
        data.searchKey = searchKey.value;
      }
      if (!isUndefined(activeTab.value)) {
        data.activeTab = activeTab.value;
      }
      const params = {
        page: pageParams.page,
        pageSize: isTableView.value ? 5 : limit,
        limit: isTableView.value ? 5 : limit,
      };
      const res = await getList(data, params);
      let newDataList = [];
      if (res.data && Array.isArray(res.data)) {
        const listTemp = otherOptions?.length
          ? otherOptions.concat(res.data)
          : res.data;
        dataList.value = listTemp;
      } else {
        const { list, pager } = res.data;
        if (
          !isTableView.value &&
          pageParams.page > 1 &&
          pageParams.page <= pager.pageCount
        ) {
          newDataList = [...dataList.value, ...list];
        } else {
          // table直接替换
          const listTemp = otherOptions?.length
            ? otherOptions.concat(list || [])
            : list || [];
          newDataList = listTemp;
        }
        dataList.value = newDataList;
        pageParams.page = pager.pageNo;
        pageParams.totalCount = pager.totalCount;
        pageParams.limit = pager.pageSize ?? pager.limit;
        finished.value = pageParams.page >= pager.pageCount;
      }
    } catch (error) {
      finished.value = true;
    }
  };

  // 手动打开select下拉列表
  const focus = () => {
    QsSelectRef?.value?.setPopupVisible(true);
  };

  // 手动关闭select下拉列表
  const blur = () => {
    QsSelectRef?.value?.setPopupVisible(false);
  };

  const resetPageChange = () => {
    pageParams.page = 1;
    getOptionList();
  };

  return {
    dataList,
    getOptionById,
    getOptionsByIdList,
    search,
    refresh,
    load,
    change,
    selectItem,
    pageParams,
    pageChange,
    tabChange,
    focus,
    blur,
    isTableView,
    changeView,
    resetPageChange,
  };
};

export default useQsSelect;
